import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Text,
} from 'grommet';
import { Refresh } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo, AppButton, DropButton, SimpleModal, InfoNotice,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import { TableWrapper } from '@Components/Partial/Product/SharedComponents.jsx';
import { paths } from '@Components/configs';
import {
  fetchNagraBatch,
  approveAndSubmitNagraBatchCandidate,
  rejectNagraBatchCandidate,
} from '@Actions';

const REVIEW_STATUS_COLORS = {
  pending: { bg: '#71717a22', border: '#71717a', text: '#71717a' },
  'in-review': { bg: '#3b82f622', border: '#3b82f6', text: '#3b82f6' },
  approved: { bg: '#00a85522', border: '#00a855', text: '#00a855' },
  rejected: { bg: '#e53e3e22', border: '#e53e3e', text: '#e53e3e' },
  submitted: { bg: '#8b5cf622', border: '#8b5cf6', text: '#8b5cf6' },
};

const StatusBadge = ({ value = null }) => {
  const colors = REVIEW_STATUS_COLORS[value] || REVIEW_STATUS_COLORS.pending;
  return (
    <Box
      background={colors.bg}
      border={{ color: colors.border, size: '1px' }}
      round="6px"
      pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
      width="fit-content"
    >
      <Text size="0.8rem" weight={600} color={colors.text} style={{ whiteSpace: 'nowrap' }}>
        {value || '—'}
      </Text>
    </Box>
  );
};

StatusBadge.propTypes = {
  value: PropTypes.string,
};

const CellText = ({ value = null, page = null }) => (
  <Text size="0.85rem" color={value ? page?.tableText : page?.secondaryText}>
    {value || '—'}
  </Text>
);

CellText.propTypes = {
  value: PropTypes.string,
  page: PropTypes.shape({ tableText: PropTypes.string, secondaryText: PropTypes.string }),
};

const headerMap = [
  { uid: 'urls', display: 'URLs', size: '14%' },
  { uid: 'screenshot', display: 'Screenshot', size: '10%' },
  { uid: 'channelName', display: 'Channel Name', size: '11%' },
  { uid: 'type', display: 'Type', size: '7%' },
  { uid: 'origin', display: 'Origin', size: '9%' },
  { uid: 'metaContentType', display: 'Content Type', size: '9%' },
  { uid: 'metaCountry', display: 'Country', size: '7%' },
  { uid: 'metaIdTimestamp', display: 'ID Timestamp', size: '11%' },
  { uid: 'reviewStatus', display: 'Status', size: '9%' },
  { uid: 'actions', display: 'Actions', size: '13%' },
];

const UrlsCell = ({ small, candidate }) => {
  const { theme: { page, button } } = useAppTheme();
  const [open, setOpen] = useState(false);
  const urlEntries = [
    { label: 'Reference URL', url: candidate.referenceUrl?.url },
    { label: 'Viewing Page URL', url: candidate.viewingPageUrl?.url },
    { label: 'Video URL', url: candidate.videoUrl?.url },
  ].filter((e) => e.url);

  if (!urlEntries.length) return <Text size="0.82rem" color={page.secondaryText}>—</Text>;

  return (
    <>
      <AppButton
        tableButtonStyle
        label={`${urlEntries.length} URL${urlEntries.length > 1 ? 's' : ''}`}
        color={button.tableText}
        bgColor={button.tableBg}
        onClick={() => setOpen(true)}
      />
      {open && (
        <SimpleModal
          small={small}
          toggleModal={() => setOpen(false)}
          modalTitle="URLs"
          hideLowerSection
          desktopMinWidth="36rem"
        >
          <Box gap="0.75rem" pad={{ vertical: '0.5rem' }}>
            {urlEntries.map(({ label, url }) => (
              <Box key={label} gap="0.25rem">
                <Text size="0.8rem" weight={600} color={page.secondaryText}>{label}</Text>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--page-highlight)',
                    textDecoration: 'underline',
                    wordBreak: 'break-all',
                    fontSize: '0.9rem',
                  }}
                >
                  {url}
                </a>
              </Box>
            ))}
          </Box>
        </SimpleModal>
      )}
    </>
  );
};

UrlsCell.propTypes = {
  small: PropTypes.bool.isRequired,
  candidate: PropTypes.shape({
    referenceUrl: PropTypes.shape({ url: PropTypes.string }),
    viewingPageUrl: PropTypes.shape({ url: PropTypes.string }),
    videoUrl: PropTypes.shape({ url: PropTypes.string }),
  }).isRequired,
};

const ScreenshotCell = ({ small, screenshotUrl }) => {
  const { theme: { page, button } } = useAppTheme();
  const [open, setOpen] = useState(false);

  if (!screenshotUrl) return <Text size="0.82rem" color={page.secondaryText}>—</Text>;

  return (
    <>
      <AppButton
        tableButtonStyle
        label="View"
        color={button.tableText}
        bgColor={button.tableBg}
        onClick={() => setOpen(true)}
      />
      {open && (
        <SimpleModal
          small={small}
          toggleModal={() => setOpen(false)}
          modalTitle="Screenshot"
          hideLowerSection
          desktopMinWidth="60rem"
        >
          <Box pad={{ vertical: '0.5rem' }} align="center">
            <img
              src={screenshotUrl}
              alt="Screenshot"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </Box>
        </SimpleModal>
      )}
    </>
  );
};

ScreenshotCell.propTypes = {
  small: PropTypes.bool.isRequired,
  screenshotUrl: PropTypes.string,
};

const RowActionDropdown = ({
  isActioning, onViewEdit, onApprove, onReject, page, button,
}) => (
  <DropButton
    disabled={isActioning}
    overridePosition={{ top: 'bottom', right: 'right' }}
    withCloseDropContent={(onClose) => (
      <Box round="6px" pad="xsmall" background={page.inputBg}>
        <AppButton
          plain
          label="View / Edit"
          color={page.inputText}
          hoverColor="#3c3c3c"
          hoverBg={button.hoverBg}
          onClick={() => { onViewEdit(); onClose(); }}
          style={{ padding: '0.3rem 0.6rem', width: '100%', textAlign: 'left' }}
        />
        <AppButton
          plain
          label="Approve & Submit"
          color="#00a855"
          hoverColor={button.hoverText}
          hoverBg={button.hoverBg}
          onClick={() => { onApprove(); onClose(); }}
          style={{ padding: '0.3rem 0.6rem', width: '100%', textAlign: 'left' }}
        />
        <AppButton
          plain
          label="Reject"
          color="#e53e3e"
          hoverColor={button.hoverText}
          hoverBg={button.hoverBg}
          onClick={() => { onReject(); onClose(); }}
          style={{ padding: '0.3rem 0.6rem', width: '100%', textAlign: 'left' }}
        />
      </Box>
    )}
  >
    {(Icon) => (
      <AppButton
        dropDown
        tableButtonStyle
        label={isActioning ? '...' : 'Actions'}
        color={button.tableText}
        fontWeight={600}
        icon={<Icon color={button.tableIcon} size="0.9rem" />}
        focusHighlight={page.focusHighlight}
        disabled={isActioning}
      />
    )}
  </DropButton>
);

RowActionDropdown.propTypes = {
  isActioning: PropTypes.bool.isRequired,
  onViewEdit: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  page: PropTypes.shape({
    inputBg: PropTypes.string,
    inputText: PropTypes.string,
    focusHighlight: PropTypes.string,
  }).isRequired,
  button: PropTypes.shape({
    tableText: PropTypes.string,
    tableIcon: PropTypes.string,
    hoverText: PropTypes.string,
    hoverBg: PropTypes.string,
  }).isRequired,
};

const STATUS_ORDER = ['pending', 'in-review', 'submitted', 'approved', 'rejected'];

const StatusTotalsGrid = ({ statusTotals, page }) => {
  if (!statusTotals || Object.keys(statusTotals).length === 0) return null;
  return (
    <Box direction="row" wrap gap="0.75rem">
      {STATUS_ORDER.map((s) => {
        const colors = REVIEW_STATUS_COLORS[s] || { bg: '#71717a22', border: '#71717a', text: '#71717a' };
        const count = statusTotals[s] ?? 0;
        return (
          <Box
            key={s}
            background={colors.bg}
            border={{ color: colors.border, size: '1px' }}
            round="10px"
            pad={{ horizontal: '1rem', vertical: '0.65rem' }}
            style={{ minWidth: '7rem' }}
          >
            <Text
              size="0.7rem"
              weight={700}
              color={colors.text}
              style={{ textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}
            >
              {s}
            </Text>
            <Text size="1.4rem" weight={600} color={page.primaryText} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {count}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};

StatusTotalsGrid.propTypes = {
  statusTotals: PropTypes.objectOf(PropTypes.number),
  page: PropTypes.shape({ primaryText: PropTypes.string }).isRequired,
};

const ActiveOperationsPills = ({ activeOperations, page }) => {
  if (!activeOperations || activeOperations.length === 0) return null;
  return (
    <Box
      border={{ color: page.tableBorder, size: '1px' }}
      round="10px"
      pad={{ horizontal: '1rem', vertical: '0.75rem' }}
      gap="0.5rem"
    >
      <Text
        size="0.7rem"
        weight={700}
        color="#00a855"
        style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
      >
        {`Active Monitoring Operations (${activeOperations.length})`}
      </Text>
      <Box direction="row" wrap gap="0.4rem">
        {activeOperations.map((op, i) => {
          const uuid = op.monitoringUuid || '';
          const label = op.label || uuid.slice(0, 8) || `op-${i + 1}`;
          return (
            <Box
              key={uuid || i}
              background="#00a85522"
              border={{ color: '#00a855', size: '1px' }}
              round="6px"
              direction="row"
              align="center"
              gap="0.3rem"
              pad={{ horizontal: '0.6rem', vertical: '0.2rem' }}
            >
              <Box width="0.45rem" height="0.45rem" round="full" background="#00a855" flex={{ shrink: 0 }} />
              <Text size="0.78rem" weight={600} color="#00a855">{label}</Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

ActiveOperationsPills.propTypes = {
  activeOperations: PropTypes.arrayOf(PropTypes.shape({
    monitoringUuid: PropTypes.string,
    label: PropTypes.string,
  })),
  page: PropTypes.shape({ tableBorder: PropTypes.string }).isRequired,
};

const AdminNagraReviewCandidatesPage = ({
  small,
  location,
  batch = [],
  batchMessage = null,
  batchStatusTotals = null,
  batchActiveOperations = null,
  loadingBatch = false,
  loadBatch,
  approveAndSubmit,
  reject,
}) => {
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const [actioningIds, setActioningIds] = useState(new Set());

  useEffect(() => {
    loadBatch();
  }, []);

  const handleApprove = async (id) => {
    setActioningIds((prev) => new Set(prev).add(id));
    await approveAndSubmit(id);
    setActioningIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleReject = async (id) => {
    setActioningIds((prev) => new Set(prev).add(id));
    await reject(id);
    setActioningIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const isEmpty = !loadingBatch && batch.length === 0;

  const renderCell = (uid, candidate, isActioning) => {
    switch (uid) {
      case 'urls': return <UrlsCell small={small} candidate={candidate} />;
      case 'screenshot': return <ScreenshotCell small={small} screenshotUrl={candidate.screenshotS3Urls?.[0]} />;
      case 'channelName': return <CellText value={candidate.channelName} page={page} />;
      case 'type': return <CellText value={candidate.type} page={page} />;
      case 'origin': return <CellText value={candidate.origin} page={page} />;
      case 'metaContentType': return <CellText value={candidate.metadata?.contentType} page={page} />;
      case 'metaCountry': return <CellText value={candidate.metadata?.country} page={page} />;
      case 'metaIdTimestamp': return <CellText value={candidate.metadata?.identificationTimestamp} page={page} />;
      case 'reviewStatus': return <StatusBadge value={candidate.reviewStatus} />;
      case 'actions':
        return (
          <RowActionDropdown
            isActioning={isActioning}
            onViewEdit={() => navigate(paths.adminNagraCandidateDetail.replace(':id', candidate.id))}
            onApprove={() => handleApprove(candidate.id)}
            onReject={() => handleReject(candidate.id)}
            page={page}
            button={button}
          />
        );
      default: return null;
    }
  };

  const tableControls = (
    <Box direction="row" align="center" gap="0.75rem">
      {!loadingBatch && (
        <Box
          background={page.altBg}
          border={{ color: page.tableBorder, size: '1px' }}
          round="12px"
          pad={{ horizontal: '0.5rem', vertical: '0.15rem' }}
        >
          <Text size="0.78rem" weight={600} color={page.secondaryText}>
            {`${batch.length} / 10`}
          </Text>
        </Box>
      )}
      <AppButton
        overrideHover
        level="tableControls"
        label="Refresh Batch"
        width="fit-content"
        icon={<Refresh size="1rem" color={isDarkMode ? button.iconColor : button.primaryText} />}
        color={button.primaryText}
        bgColor={button.primaryBg}
        onClick={() => loadBatch()}
        disabled={loadingBatch}
      />
    </Box>
  );

  return (
    <AdminNavWrapper
      small={small}
      bannerProps={{
        title: 'Review Candidates',
        subTitle: 'Nagra Monitoring',
      }}
      location={location}
    >
      <Seo title="Review Candidates | Nagra Monitoring" />
      <Box direction="column" gap="1rem" pad="1.5rem">
        <InfoNotice
          noMargin
          header="How to review candidates"
        >
          <Box direction="column" gap="0.5rem">
            <Text size="0.95rem" color={page.primaryText}>
              Review and action the current batch of pending piracy candidates. When you refresh the batch,
              you will receive up to 10 of the oldest pending candidates in the queue, assigned to your user ID.
              Your assigned batch is preserved if you navigate to the View / Edit page and return.
            </Text>
            <Text size="0.95rem" color={page.primaryText}>
              Approve, reject, or view/edit each candidate — assess the details carefully.
              You may also approve or reject from the view/edit page if you need to
              add or edit information prior to actioning.
            </Text>
            <Text size="0.95rem" color={page.secondaryText}>
              Please note: channel names may be missing and will need to be identified before approving — use the
              View / Edit page to add this information prior to submitting your review. Please ensure that you
              review screenshots carefully as well.
            </Text>
          </Box>
        </InfoNotice>
        <StatusTotalsGrid statusTotals={batchStatusTotals} page={page} />
        <ActiveOperationsPills activeOperations={batchActiveOperations} page={page} />
        <TableWrapper
          small={small}
          header="Current Batch"
          tableControls={tableControls}
          hidePagination
        >
          {loadingBatch && (
            <Box align="center" justify="center" pad="2.5rem">
              <Text size="0.95rem" color={page.secondaryText}>Loading batch...</Text>
            </Box>
          )}
          {isEmpty && (
            <Box align="center" justify="center" pad="2.5rem" gap="0.5rem">
              <Text size="0.95rem" color={page.secondaryText}>
                {batchMessage || 'No candidates in the review queue.'}
              </Text>
            </Box>
          )}
          {!loadingBatch && batch.length > 0 && (
            <Box fill="horizontal">
              <Box
                direction="row"
                fill="horizontal"
                border={{ side: 'bottom', color: page.tableBorder, size: '1px' }}
                background={page.altBg}
              >
                {headerMap.map(({ uid, display, size }) => (
                  <Box key={uid} width={size} pad={{ horizontal: '0.85rem', vertical: '0.6rem' }}>
                    <Text
                      size="0.78rem"
                      weight={600}
                      color={page.secondaryText}
                      style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                    >
                      {display}
                    </Text>
                  </Box>
                ))}
              </Box>
              {batch.map((candidate) => {
                const isActioning = actioningIds.has(candidate.id);
                return (
                  <Box
                    key={candidate.id}
                    direction="row"
                    fill="horizontal"
                    align="center"
                    background={page.tableBg}
                    border={{ side: 'bottom', color: page.tableBorder, size: '1px' }}
                  >
                    {headerMap.map(({ uid, size }) => (
                      <Box key={uid} width={size} pad={{ horizontal: '0.85rem', vertical: '0.65rem' }}>
                        {renderCell(uid, candidate, isActioning)}
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          )}
        </TableWrapper>
      </Box>
    </AdminNavWrapper>
  );
};

AdminNagraReviewCandidatesPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  batch: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    referenceUrl: PropTypes.shape({ url: PropTypes.string }),
    viewingPageUrl: PropTypes.shape({ url: PropTypes.string }),
    videoUrl: PropTypes.shape({ url: PropTypes.string }),
    channelName: PropTypes.string,
    type: PropTypes.string,
    origin: PropTypes.string,
    metadata: PropTypes.shape({
      contentType: PropTypes.string,
      country: PropTypes.string,
      identificationTimestamp: PropTypes.string,
    }),
    reviewStatus: PropTypes.string,
    screenshotS3Urls: PropTypes.arrayOf(PropTypes.string),
  })),
  batchMessage: PropTypes.string,
  batchStatusTotals: PropTypes.objectOf(PropTypes.number),
  batchActiveOperations: PropTypes.arrayOf(PropTypes.shape({
    monitoringUuid: PropTypes.string,
    label: PropTypes.string,
  })),
  loadingBatch: PropTypes.bool,
  loadBatch: PropTypes.func.isRequired,
  approveAndSubmit: PropTypes.func.isRequired,
  reject: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    batch: state.nagraCandidates.batch,
    batchMessage: state.nagraCandidates.batchMessage,
    batchStatusTotals: state.nagraCandidates.batchStatusTotals,
    batchActiveOperations: state.nagraCandidates.batchActiveOperations,
    loadingBatch: state.nagraCandidates.loadingBatch,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    loadBatch: fetchNagraBatch,
    approveAndSubmit: approveAndSubmitNagraBatchCandidate,
    reject: rejectNagraBatchCandidate,
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminNagraReviewCandidatesPage);
